import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Video, X, Circle, Square, RotateCcw, Check, Camera } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;
const MAX_SECONDS = 30;

export default function ShortRecorder({ onUploaded }) {
  const { getAuthHeader } = useAuth();
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle | requesting | ready | recording | preview | uploading
  const [error, setError] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(MAX_SECONDS);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [facing, setFacing] = useState("environment"); // back camera default

  const videoLiveRef = useRef(null);
  const videoPreviewRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const tickRef = useRef(null);
  const stopTimeoutRef = useRef(null);

  const teardownStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const cleanup = () => {
    if (tickRef.current) clearInterval(tickRef.current);
    if (stopTimeoutRef.current) clearTimeout(stopTimeoutRef.current);
    teardownStream();
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  };

  useEffect(() => () => cleanup(), []); // unmount cleanup
  // eslint-disable-next-line react-hooks/exhaustive-deps

  const requestCamera = async (preferredFacing = facing) => {
    setError(null);
    setPhase("requesting");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: preferredFacing }, width: { ideal: 1080 }, height: { ideal: 1920 } },
        audio: true,
      });
      streamRef.current = stream;
      if (videoLiveRef.current) {
        videoLiveRef.current.srcObject = stream;
        videoLiveRef.current.play().catch(() => {});
      }
      setPhase("ready");
    } catch (err) {
      console.error("getUserMedia failed", err);
      setError(err?.message || "Camera/mic permission denied. Please allow access and try again.");
      setPhase("idle");
    }
  };

  const startRecording = () => {
    if (!streamRef.current) return;
    chunksRef.current = [];
    // Prefer mp4 → fall back to webm if not supported
    let mimeType = "video/mp4";
    if (!window.MediaRecorder?.isTypeSupported(mimeType)) mimeType = "video/webm;codecs=vp9,opus";
    if (!window.MediaRecorder?.isTypeSupported(mimeType)) mimeType = "video/webm";
    try {
      const rec = new MediaRecorder(streamRef.current, { mimeType });
      recorderRef.current = rec;
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: chunksRef.current[0]?.type || mimeType });
        const url = URL.createObjectURL(blob);
        setPreviewUrl(url);
        setPhase("preview");
      };
      rec.start(1000);
      setPhase("recording");
      setSecondsLeft(MAX_SECONDS);
      tickRef.current = setInterval(() => {
        setSecondsLeft((s) => {
          if (s <= 1) { clearInterval(tickRef.current); return 0; }
          return s - 1;
        });
      }, 1000);
      stopTimeoutRef.current = setTimeout(() => stopRecording(), MAX_SECONDS * 1000);
    } catch (e) {
      setError(e?.message || "Recording failed to start");
      setPhase("ready");
    }
  };

  const stopRecording = () => {
    if (tickRef.current) clearInterval(tickRef.current);
    if (stopTimeoutRef.current) clearTimeout(stopTimeoutRef.current);
    try { recorderRef.current?.state !== "inactive" && recorderRef.current?.stop(); } catch (_) {}
  };

  const retake = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    chunksRef.current = [];
    setPhase("ready");
    if (videoLiveRef.current && streamRef.current) {
      videoLiveRef.current.srcObject = streamRef.current;
      videoLiveRef.current.play().catch(() => {});
    }
  };

  const flipCamera = async () => {
    const next = facing === "environment" ? "user" : "environment";
    setFacing(next);
    teardownStream();
    await requestCamera(next);
  };

  const uploadShort = async () => {
    if (!previewUrl) return;
    setPhase("uploading");
    try {
      const resp = await fetch(previewUrl);
      const blob = await resp.blob();
      const ext = blob.type.includes("mp4") ? "mp4" : "webm";
      const filename = `short-${Date.now()}.${ext}`;
      const formData = new FormData();
      formData.append("file", new File([blob], filename, { type: blob.type }));
      const res = await axios.post(`${API}/upload?category=shorts`, formData, {
        headers: { ...getAuthHeader() },
      });
      const uploadedUrl = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
      onUploaded?.(uploadedUrl, { ...res.data, content_type: blob.type });
      toast.success("Short uploaded and added to your listing");
      handleClose();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Upload failed");
      setPhase("preview");
    }
  };

  const handleOpen = () => {
    setOpen(true);
    setSecondsLeft(MAX_SECONDS);
    requestCamera();
  };

  const handleClose = () => {
    cleanup();
    setOpen(false);
    setPhase("idle");
    setError(null);
    setPreviewUrl(null);
    chunksRef.current = [];
  };

  return (
    <>
      <Button
        onClick={handleOpen}
        variant="outline"
        className="border-[#EC4899]/40 text-[#EC4899] hover:bg-[#EC4899]/10"
        data-testid="short-recorder-open-btn"
      >
        <Camera className="w-4 h-4 mr-2" /> Record a Short
      </Button>

      {open && (
        <div className="fixed inset-0 z-[120] bg-black/95 backdrop-blur-xl flex items-center justify-center p-4" data-testid="short-recorder-modal">
          <div className="relative w-full max-w-sm aspect-[9/16] rounded-3xl overflow-hidden bg-black border-2 border-[#EC4899]/40 shadow-2xl flex flex-col">
            {/* Close button */}
            <button
              type="button"
              onClick={handleClose}
              className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center"
              data-testid="short-recorder-close-btn"
            >
              <X className="w-5 h-5 text-white" />
            </button>

            {/* Flip camera button (only when ready) */}
            {phase === "ready" && (
              <button
                type="button"
                onClick={flipCamera}
                className="absolute top-3 left-3 z-10 w-9 h-9 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center"
                data-testid="short-recorder-flip-btn"
              >
                <RotateCcw className="w-5 h-5 text-white" />
              </button>
            )}

            {/* Live camera or preview */}
            {phase !== "preview" && phase !== "uploading" && (
              <video
                ref={videoLiveRef}
                playsInline
                muted
                className="absolute inset-0 w-full h-full object-cover"
              />
            )}
            {phase === "preview" && previewUrl && (
              <video
                ref={videoPreviewRef}
                src={previewUrl}
                playsInline
                controls
                autoPlay
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="short-recorder-preview-video"
              />
            )}

            {/* Recording indicator + timer */}
            {phase === "recording" && (
              <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/90">
                <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                <span className="text-white text-xs font-semibold">REC · {secondsLeft}s</span>
              </div>
            )}

            {/* Idle / error state */}
            {(phase === "idle" || phase === "requesting") && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 z-20 bg-black/70">
                <Video className="w-12 h-12 text-[#EC4899] mb-3" />
                <p className="text-white font-medium mb-1">
                  {phase === "requesting" ? "Requesting camera…" : "Tap to start"}
                </p>
                <p className="text-[#94A3B8] text-sm mb-4 max-w-xs">
                  Record up to {MAX_SECONDS}s of vertical video — straight to your listing.
                </p>
                {error && <p className="text-red-400 text-xs mb-3">{error}</p>}
                {phase === "idle" && (
                  <Button onClick={() => requestCamera()} className="bg-[#EC4899] hover:bg-[#DB2777] text-white" data-testid="short-recorder-grant-btn">
                    <Camera className="w-4 h-4 mr-2" /> Allow Camera & Mic
                  </Button>
                )}
              </div>
            )}

            {/* Bottom controls */}
            <div className="absolute bottom-0 left-0 right-0 z-10 p-4 flex items-center justify-center gap-4">
              {phase === "ready" && (
                <button
                  type="button"
                  onClick={startRecording}
                  className="w-16 h-16 rounded-full bg-white flex items-center justify-center hover:scale-105 transition-transform active:scale-95"
                  aria-label="Record"
                  data-testid="short-recorder-start-btn"
                >
                  <span className="w-12 h-12 rounded-full bg-red-500" />
                </button>
              )}
              {phase === "recording" && (
                <button
                  type="button"
                  onClick={stopRecording}
                  className="w-16 h-16 rounded-full bg-white flex items-center justify-center hover:scale-105 transition-transform active:scale-95"
                  aria-label="Stop"
                  data-testid="short-recorder-stop-btn"
                >
                  <Square className="w-7 h-7 text-red-500 fill-red-500" />
                </button>
              )}
              {phase === "preview" && (
                <div className="flex items-center gap-3">
                  <Button onClick={retake} variant="outline" className="border-white/20 text-white hover:bg-white/10" data-testid="short-recorder-retake-btn">
                    <RotateCcw className="w-4 h-4 mr-2" /> Retake
                  </Button>
                  <Button onClick={uploadShort} className="bg-gradient-to-r from-[#EC4899] to-[#7C3AED] hover:from-[#7C3AED] hover:to-[#EC4899] text-white font-semibold" data-testid="short-recorder-use-btn">
                    <Check className="w-4 h-4 mr-2" /> Use This Short
                  </Button>
                </div>
              )}
              {phase === "uploading" && (
                <div className="flex items-center gap-2 text-white">
                  <Circle className="w-4 h-4 animate-spin" /> Uploading…
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
